import PubSub from 'pubsub-js';
import { createContext, memo, useContext, useState } from 'react';
import { AppEvent } from '../../helpers/unityPubSub';
import { usePubSubEvent } from '../../hooks/usePubSubEvent';

const DownloadFileContext = createContext({
  isOpen: false,
  ids: [],
  downloads: {}, // key === section id // value section
  uploads: {},
  uploadIds: [],
  close: () => {},
});

export const SECTION = {
  DEFAULT: 'DEFAULT',
}

const DownloadFileContextProvider = memo(({ children }) => {
  // TODO: reset everything when user loggs out
  const [isOpen, setIsOpen] = useState(false);

  // downloads
  // ________________
  // ________________
  // ________________
  // ________________
  const [ids, setIds] = useState([]);
  const [downloads, setDownloads] = useState({});

  const startDownload = (download, sectionName) => {
    setIsOpen(true)
    setIds((ids) => {
      return [...ids, download.id];
    });

    setDownloads((downloads) => {
      let downloadsCopy = { ...downloads };

      if (!downloadsCopy[download.sectionId]) {
        // add new section
        downloadsCopy[download.sectionId] = {
          id: download.sectionId,
          name: sectionName,
          items: [],
        };
      }

      const section = downloadsCopy[download.sectionId];

      downloadsCopy = {
        ...downloadsCopy,
        [download.sectionId]: {
          ...section,
          items: [...section.items, download],
        },
      };

      return downloadsCopy;
    });
  };

  usePubSubEvent(AppEvent.DOWNLOAD_START, (_, { data, sectionName}) => {
    startDownload(data, sectionName)
  })

  const updateDownload = (
    sectionId,
    downloadId,
    data,
  ) => {
    // TODO: do batch update here -> write data to some intermediate array and only after few miliseconds update variable

    setDownloads(downloads => {
      if (!downloads[sectionId]) return downloads;

      const updatedDownloads = downloads[sectionId].items.map((download) => {
        return download.id === downloadId 
          ? { ...download, ...data }
          : download;
      });

      return {
        ...downloads,
        [sectionId]: {
          ...downloads[sectionId],
          items: updatedDownloads,
        },
      };
    })
  };

  usePubSubEvent(AppEvent.DOWNLOAD_UPDATE, (_, { sectionId, downloadId, data }) => {
    updateDownload(sectionId, downloadId, data)
  })


  usePubSubEvent(AppEvent.DOWNLOAD_FAILED, (_, { sectionId, downloadId, data }) => {
    updateDownload(sectionId, downloadId, { __FAILED__: true });
  })


  // ________________
  // ________________
  // ________________
  // ________________
  // UPLOADS 
  // ________________
  const [uploadIds, setUploadIds] = useState([]);
  const [uploads, setUploads] = useState({});

  const startUpload = (upload, sectionName) => {
    setIsOpen(true)
    setUploadIds((ids) => {
      return [...ids, upload.id];
    });

    setUploads((uploads) => {
      let uploadsCopy = { ...uploads };

      if (!uploadsCopy[upload.sectionId]) {
        // add new section
        uploadsCopy[upload.sectionId] = {
          id: upload.sectionId,
          name: sectionName,
          items: [],
        };
      }

      const section = uploadsCopy[upload.sectionId];

      uploadsCopy = {
        ...uploadsCopy,
        [upload.sectionId]: {
          ...section,
          items: [...section.items, upload],
        },
      };

      return uploadsCopy;
    });
  };

  usePubSubEvent(AppEvent.UPLOAD_START, (_, { data, sectionName}) => {
    startUpload(data, sectionName)
  })

  const updateUpload = (
    sectionId,
    uploadId,
    data,
  ) => {
    // TODO: do batch update here -> write data to some intermediate array and only after few miliseconds update variable

    
    
    setUploads(uploads => {
      if (!uploads[sectionId]) return uploads;

      const updatedUploads = uploads[sectionId].items.map((upload) => {
        return upload.id === uploadId 
          ? { ...upload, ...data }
          : upload;
      });

      return {
        ...uploads,
        [sectionId]: {
          ...uploads[sectionId],
          items: updatedUploads,
        },
      };
    })
  };

  usePubSubEvent(AppEvent.UPLOAD_UPDATE, (_, { sectionId, uploadId, data }) => {
    updateUpload(sectionId, uploadId, data)
  })


  usePubSubEvent(AppEvent.UPLOAD_FAILED, (_, { sectionId, uploadId, data }) => {
    updateUpload(sectionId, uploadId, { __FAILED__: true });
  })
  // ________________
  // ________________
  // ________________
  // ________________

  const close = ({ removeAttachments = true, section } = {}) => {
    if (removeAttachments && section === 'downloads') {
      setDownloads({});
      setIds([]);
    } else if (removeAttachments && section === 'uploads') {
      setUploads({})
      setUploadIds([]);
    } else if (removeAttachments) {
      setIsOpen(false);
      setDownloads({});
      setIds([]);
      setUploads({})
      setUploadIds([]);
    }
  };


  const contextValue = {
    isOpen,
    ids,
    close,
    downloads,
    uploads,
    uploadIds,
    // downloads: {
    //   test: {
    //     id: 'test',
    //     name: 'test section',
    //     items: [
    //       {
    //         id: '22',
    //         fileName: 'this is filename.svg',
    //         fileSizeBytes: 200,
    //         downloadedBytes: 2,
    //         sectionId: 'test',
    //       },
    //       {
    //         id: '23',
    //         fileName: 'this is filename.svg',
    //         fileSizeBytes: 200,
    //         downloadedBytes: 2,
    //         sectionId: 'test',
    //       },
    //       {
    //         id: '24',
    //         fileName: 'this is filename.svg',
    //         fileSizeBytes: 200,
    //         downloadedBytes: 2,
    //         sectionId: 'test',
    //       },
    //     ],
    //   },
    // },
  };

  return (
    <DownloadFileContext.Provider value={contextValue}>{children}</DownloadFileContext.Provider>
  );
});

export const startDownload = ({ id, fileName, fileSizeBytes, downloadedBytes, sectionId }, sectionName) => {
  const data = { id, fileName, fileSizeBytes, downloadedBytes, sectionId }
  PubSub.publish(AppEvent.DOWNLOAD_START, { data, sectionName });
};

export const updateDownload = (sectionId, downloadId, data) => {
  PubSub.publish(AppEvent.DOWNLOAD_UPDATE, { sectionId, downloadId, data });
};

export const downloadFailed = (sectionId, downloadId, data) => {
  PubSub.publish(AppEvent.DOWNLOAD_FAILED, { sectionId, downloadId, data });
};

export const startUpload = ({ id, fileName, fileSizeBytes, downloadedBytes, sectionId }, sectionName) => {
  const data = { id, fileName, fileSizeBytes, downloadedBytes, sectionId }
  PubSub.publish(AppEvent.UPLOAD_START, { data, sectionName });
};

export const updateUpload = (sectionId, uploadId, data) => {
  PubSub.publish(AppEvent.UPLOAD_UPDATE, { sectionId, uploadId, data });
};

export const uploadFailed = (sectionId, uploadId, data) => {
  PubSub.publish(AppEvent.UPLOAD_FAILED, { sectionId, uploadId, data });
};

const useDownloadFileContext = () => useContext(DownloadFileContext);
export { DownloadFileContextProvider, useDownloadFileContext };
